import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Users, Camera, Package, MessageCircle, Instagram, Music2, Heart, Star, CalendarHeart, Check } from "lucide-react";
import { formatRupiah, formatRupiahShort } from "../lib/format";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import { VENDORS } from "../lib/vendors";
import { useOwnerEditMode, OwnerEditToggle, EditableText, EditableImage } from "../components/OwnerEdit";
import { api } from "../lib/api";
import { distinctiveItems } from "../lib/packages";
import { faqJsonLd } from "../lib/landingContent";
import WhyDwg from "../components/WhyDwg";
import FaqSection from "../components/FaqSection";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80";
const HERO_PHOTOS = [
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=800&q=80",
];
const DEFAULT_HEADLINE = "Hari istimewa Anda, dirancang oleh satu tim.";
const DEFAULT_LEAD = "Wedding organizer, MUA, dekorasi, foto & video, attire, hingga musik — semua dikerjakan tim Dessy Wedding Group sejak 2015 di Garut, Bandung & sekitarnya. Satu koordinasi, satu penanggung jawab, tanpa repot.";
const LEGACY_TITLES = ["Dessy Wedding Group", "Wedding & Event Services"];
const pick = (v, d) => (v && !LEGACY_TITLES.includes(v) ? v : d);

const SEO_DESC = "Dessy Wedding Group — wedding organizer, MUA, dekorasi, foto & video di Garut dan Bandung. Paket wedding all-in, akad, intimate (Intimade 50–300 pax), khitan & prewedding. Konsultasi gratis.";
const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Dessy Wedding Group",
  url: "https://dessywedding.site",
  logo: "https://dessywedding.site/brand/dwg-logo.png",
  image: "https://dessywedding.site/brand/dwg-logo.png",
  telephone: "+628112225912",
  foundingDate: "2015",
  areaServed: ["Garut", "Bandung"],
  address: { "@type": "PostalAddress", addressLocality: "Garut", addressRegion: "Jawa Barat", addressCountry: "ID" },
  sameAs: ["https://instagram.com/mua.dessywedding", "https://www.tiktok.com/@dessyweddinggarut.mua"],
};

export default function Landing() {
  const nav = useNavigate();
  const { isOwner, editMode, setEnabled } = useOwnerEditMode();
  const [allBundling, setAllBundling] = useState([]);
  const [stats, setStats] = useState(null);
  const [vendorCounts, setVendorCounts] = useState({});
  const [vendorRanges, setVendorRanges] = useState({});
  const [vendorImages, setVendorImages] = useState({});
  const [cms, setCms] = useState({
    hero_title: "", hero_subtitle: "",
    contact_wa: "628112225912", contact_ig: "mua.dessywedding", contact_tiktok: "dessyweddinggarut.mua",
    promo_title: "", promo_body: "", testimonials: [], gallery_urls: [], hero_image_url: null, cta_wa_message: "",
  });

  const applyCms = (data) => {
    setCms((prev) => ({ ...prev, ...data }));
    const map = {};
    Object.entries(data?.vendor_images_resolved || {}).forEach(([slug, v]) => {
      if (v?.url) map[slug] = `${BACKEND_URL}${v.url}`;
    });
    setVendorImages(map);
  };

  useEffect(() => {
    publicApi.get("/public/catalog", { params: { bundling: true } })
      .then(({ data }) => setAllBundling(data || [])).catch(() => {});
    publicApi.get("/public/catalog", { params: { bundling: false } })
      .then(({ data }) => {
        const c = {}, r = {};
        (data || []).forEach((p) => {
          c[p.division] = (c[p.division] || 0) + 1;
          const price = p.current_price ?? p.normal_price ?? 0;
          if (price > 0) {
            const cur = r[p.division] || { min: Infinity, max: 0 };
            r[p.division] = { min: Math.min(cur.min, price), max: Math.max(cur.max, price) };
          }
        });
        setVendorCounts(c);
        setVendorRanges(r);
      }).catch(() => {});
    publicApi.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
    publicApi.get("/public/cms/landing").then(({ data }) => applyCms(data)).catch(() => {});
  }, []);

  const bundling = allBundling.slice(0, 3);
  const highlights = useMemo(() => distinctiveItems(allBundling), [allBundling]);
  const cmsAsset = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
  const featuredVendors = VENDORS.filter((v) => v.featured).sort((a, b) => a.order - b.order);

  const saveCms = async (patch) => {
    await api.put("/cms/landing?mode=publish", patch);
    const { data } = await publicApi.get("/public/cms/landing");
    applyCms(data);
  };
  const saveText = (field) => (val) => saveCms({ [field]: val });
  const saveHeroImage = async (media_id) => saveCms({ hero_image_id: media_id });

  const fmtStat = (n) => (n == null ? "—" : n >= 100 ? `${Math.floor(n / 10) * 10}+` : String(n));
  const STATS = [
    [fmtStat(stats?.packages), "Paket aktif"],
    [VENDORS.length, "Lini layanan"],
    [fmtStat(stats?.clients), "Klien dilayani"],
    [new Date().getFullYear() - 2015 + "+", "Tahun pengalaman"],
  ];

  return (
    <div className="min-h-screen bg-[#F7F1E8] text-[#1E1611] w-full max-w-full overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="landing-page" data-public-page="true">
      <Seo description={SEO_DESC} path="/" jsonLd={[JSON_LD, faqJsonLd()]} />
      <style>{`
        .font-serif { font-family: 'Cormorant Garamond', serif; }
        .gold-line { background: linear-gradient(90deg, transparent 0%, #C4A57B 20%, #C4A57B 80%, transparent 100%); height: 1px; }
        .luxury-shadow { box-shadow: 0 20px 60px -20px rgba(139, 90, 42, 0.35); }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .marquee { animation: marquee 38s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .marquee { animation: none; } }
      `}</style>

      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-px bg-[#C4A57B]"/>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold" data-testid="hero-eyebrow">Wedding Organizer & MUA · Garut — Bandung</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#1E1611]" data-testid="hero-title">
              <EditableText editMode={editMode} value={pick(cms.hero_title, DEFAULT_HEADLINE)}
                onSave={saveText("hero_title")} testid="edit-hero-title"/>
            </motion.h1>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, delay: 0.35 }} style={{ originX: 0 }} className="mt-8 gold-line max-w-xs"/>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 text-[#5C4A3D] text-sm md:text-lg leading-relaxed max-w-lg font-light" data-testid="hero-subtitle">
              <EditableText editMode={editMode} multiline value={pick(cms.hero_subtitle, DEFAULT_LEAD)}
                onSave={saveText("hero_subtitle")} testid="edit-hero-subtitle"/>
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-10 flex flex-wrap gap-3">
              <Link to="/paket-wedding-dwg" className="inline-flex items-center gap-2 bg-[#1E1611] text-[#F7F1E8] px-7 py-3.5 rounded-full font-medium hover:bg-[#8B5A2A] transition-colors duration-300 group text-xs tracking-[0.15em] uppercase" data-testid="btn-hero-paket-dwg">
                <Package className="w-4 h-4"/> Lihat Paket Wedding <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"/>
              </Link>
              <Link to="/consultation" className="inline-flex items-center gap-2 bg-[#C4A57B] text-[#1E1611] hover:bg-[#8B5A2A] hover:text-white transition-colors px-7 py-3.5 rounded-full font-semibold text-xs tracking-[0.15em] uppercase" data-testid="btn-hero-consult">
                <CalendarHeart className="w-4 h-4"/> Konsultasi Gratis
              </Link>
              <Link to="/vendor" className="inline-flex items-center gap-2 border border-[#1E1611] text-[#1E1611] px-7 py-3.5 rounded-full font-medium hover:bg-[#1E1611] hover:text-white transition-colors text-xs tracking-[0.15em] uppercase" data-testid="btn-hero-vendor">
                Vendor & Harga
              </Link>
            </motion.div>
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-xl" data-testid="hero-stats">
              {STATS.map(([n, l], i) => (
                <motion.div key={l} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 + i * 0.1 }}>
                  <div className="font-serif text-4xl font-medium text-[#8B5A2A]" data-testid={`hero-stat-${i}`}>{n}</div>
                  <div className="text-[10px] text-[#5C4A3D] mt-1 uppercase tracking-[0.2em]">{l}</div>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="relative">
            {cms.hero_image_url ? (
              <EditableImage editMode={editMode}
                src={cmsAsset(cms.hero_image_url)} alt="Pengantin Dessy Wedding Group"
                imageClassName="w-full h-[420px] md:h-[560px] object-cover luxury-shadow rounded-tl-[60px] rounded-br-[60px]"
                onUploaded={saveHeroImage} label="Ganti Foto Hero" testid="edit-hero-image"/>
            ) : (
              <div className={`${editMode ? "outline-2 outline-dashed outline-[#C4A57B] outline-offset-2 rounded-xl relative cursor-pointer" : ""}`}
                onClick={editMode ? () => document.getElementById("hero-image-input")?.click() : undefined}
                data-testid="edit-hero-image-grid">
                <div className="grid grid-cols-2 gap-4">
                  {HERO_PHOTOS.map((src, i) => (
                    <motion.img key={src} src={src} alt="Dokumentasi wedding Dessy Wedding Group"
                      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: i % 2 ? 32 : 0 }} transition={{ duration: 0.9, delay: 0.3 + i * 0.12 }}
                      className={`w-full h-56 md:h-72 object-cover luxury-shadow ${i === 0 ? "rounded-tl-[60px]" : i === 1 ? "rounded-tr-[60px]" : i === 2 ? "rounded-bl-[60px]" : "rounded-br-[60px]"}`}
                      loading="lazy"/>
                  ))}
                </div>
                {editMode && (
                  <>
                    <input id="hero-image-input" type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const fd = new FormData(); fd.append("file", f); fd.append("image_category", "Hero");
                        try {
                          const { data } = await api.post("/cms/landing/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          await saveHeroImage(data.id);
                        } catch { /* toast handled by EditableImage */ }
                      }}/>
                    <div className="absolute top-3 right-3 bg-[#8B5A2A] text-white px-3 py-2 rounded-full text-[10px] uppercase tracking-widest font-semibold shadow-lg">
                      <Camera className="w-3.5 h-3.5 inline mr-1"/> Upload Hero
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="absolute -right-16 top-1/3 w-72 h-72 rounded-full bg-[#C4A57B]/20 blur-3xl -z-10"/>
          </motion.div>
        </div>
      </section>

      <WhyDwg />

      {/* Ecosystem marquee */}
      <section className="border-y border-[#E5D9C4] bg-[#EFE6D3] overflow-hidden" data-testid="section-ecosystem">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-4">
          <Reveal className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-1">Ekosistem DWG</div>
              <h2 className="font-serif text-2xl md:text-3xl">{VENDORS.length} lini layanan, satu koordinasi.</h2>
            </div>
            <Link to="/vendor" className="text-xs uppercase tracking-widest font-semibold text-[#8B5A2A] hover:text-[#1E1611] inline-flex items-center gap-1" data-testid="ecosystem-all-vendors">
              Jelajahi semua <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </Reveal>
        </div>
        <div className="relative py-6">
          <div className="flex w-max marquee gap-10 text-lg font-serif">
            {[...VENDORS, ...VENDORS].map((v, i) => (
              <Link key={`${v.slug}-${i}`} to={`/vendor/${v.slug}`} className="flex items-center gap-2 text-[#3A2E28] hover:text-[#8B5A2A] transition-colors whitespace-nowrap">
                <Star className="w-2.5 h-2.5 text-[#8B5A2A] fill-[#8B5A2A]"/> {v.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Preview: Paket Wedding DWG */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="section-preview-paket-dwg">
        <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-2">Paket Bundling</div>
            <h2 className="font-serif text-3xl md:text-5xl font-medium">Paket Wedding DWG</h2>
            <p className="mt-3 text-[#5C4A3D] font-light max-w-xl text-sm md:text-base">
              Gabungan MUA, dekorasi, foto/video, dan organizer dalam satu paket — harga lebih hemat dibanding pesan terpisah.
            </p>
          </div>
          <Link to="/paket-wedding-dwg" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#8B5A2A] hover:text-[#1E1611] transition-colors" data-testid="preview-paket-dwg-all">
            Lihat Semua Paket <ArrowRight className="w-3.5 h-3.5"/>
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bundling.length === 0 && (
            <div className="col-span-full text-center py-10 text-[#8B5A2A] font-serif italic">Memuat paket bundling...</div>
          )}
          {bundling.map((p, i) => {
            const hasPromo = p.promo_active && p.promo_price > 0;
            const items = highlights(p).slice(0, 3);
            return (
              <Reveal key={p.product_id} delay={i * 0.1}>
                <button onClick={() => nav(`/catalog/${p.product_id}?from=dwg`)}
                  className="text-left w-full h-full bg-white rounded-2xl overflow-hidden luxury-shadow hover:-translate-y-1 transition-transform duration-500 group flex flex-col"
                  data-testid={`preview-pkg-${p.product_id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={resolveImg(p.card_image) || FALLBACK_IMG} alt={p.product_name} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"/>
                    {hasPromo && <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] bg-rose-600 text-white px-3 py-1.5 rounded-full font-semibold">Promo</span>}
                    <div className="absolute bottom-3 left-4 right-4 text-white font-serif text-xl leading-tight">{p.product_name}</div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    {items.length > 0 && (
                      <div className="space-y-1 mb-4">
                        {items.map((it) => (
                          <div key={it} className="flex items-start gap-1.5 text-[11px] text-[#3A2E28]">
                            <Check className="w-3 h-3 text-[#8B5A2A] shrink-0 mt-0.5"/><span className="line-clamp-1">{it}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-auto text-[9px] uppercase tracking-[0.2em] text-[#8B5A2A] font-semibold">{hasPromo ? "Harga promo" : "Harga paket"}</div>
                    {hasPromo && <div className="text-[11px] text-[#8C7A5C] line-through">{formatRupiah(p.normal_price)}</div>}
                    <div className={`font-serif font-medium text-2xl mt-0.5 ${hasPromo ? "text-rose-700" : "text-[#1E1611]"}`} data-testid={`preview-pkg-price-${p.product_id}`}>
                      {formatRupiah(hasPromo ? p.promo_price : p.normal_price)}
                    </div>
                    <div className="mt-3 text-[10px] uppercase tracking-widest text-[#8B5A2A] inline-flex items-center gap-1 font-semibold group-hover:translate-x-1 transition-transform">
                      Detail Paket <ArrowRight className="w-3 h-3"/>
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Preview: Vendor unggulan */}
      <section className="bg-[#EFE6D3] border-y border-[#E5D9C4] py-16" data-testid="section-preview-vendor">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-2">Vendor & Harga</div>
              <h2 className="font-serif text-3xl md:text-5xl font-medium">Pilih vendor sesuai kebutuhan</h2>
              <p className="mt-3 text-[#5C4A3D] font-light max-w-xl text-sm md:text-base">
                Butuh satu layanan saja? Setiap vendor DWG punya katalog & harga sendiri.
              </p>
            </div>
            <Link to="/vendor" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#8B5A2A] hover:text-[#1E1611] transition-colors" data-testid="preview-vendor-all">
              Semua Vendor <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredVendors.map((v, i) => {
              const count = vendorCounts[v.division_id] || 0;
              const soon = count === 0;
              const range = vendorRanges[v.division_id];
              return (
                <Reveal key={v.slug} delay={(i % 3) * 0.1}>
                  <Link to={soon ? `/consultation?vendor=${encodeURIComponent(v.name)}` : `/vendor/${v.slug}`}
                    className="bg-white rounded-2xl overflow-hidden luxury-shadow hover:-translate-y-1 transition-transform duration-500 group flex flex-col h-full"
                    data-testid={`preview-vendor-${v.slug}`}>
                    <div className="relative h-40 overflow-hidden">
                      <img src={vendorImages[v.slug] || v.image} alt={v.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"/>
                      <div className="absolute top-3 left-3">
                        {soon ? (
                          <span className="text-[9px] uppercase tracking-[0.2em] bg-[#8C7A5C] text-white px-3 py-1.5 rounded-full font-semibold">Segera Hadir</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-[0.2em] bg-white/95 text-[#1E1611] px-3 py-1.5 rounded-full font-semibold">{count} paket</span>
                        )}
                      </div>
                      <div className="absolute bottom-3 left-4 right-4 text-white">
                        <div className="text-[9px] uppercase tracking-widest text-[#C4A57B]">{v.tagline}</div>
                        <div className="font-serif text-xl leading-tight mt-0.5">{v.name}</div>
                      </div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <p className="text-xs text-[#5C4A3D] font-light line-clamp-2 flex-1">{v.description}</p>
                      {range && !soon && (
                        <div className="mt-3 pt-3 border-t border-[#F0EBE0]" data-testid={`preview-vendor-${v.slug}-range`}>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-[#8B5A2A] font-semibold">Mulai dari</div>
                          <div className="font-serif text-lg text-[#1E1611] leading-tight">
                            {formatRupiah(range.min)}
                            {range.max > range.min && (
                              <span className="text-xs text-[#8C7A5C] font-sans font-light"> s/d {formatRupiahShort(range.max)}</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="mt-3 text-[10px] uppercase tracking-widest text-[#8B5A2A] inline-flex items-center gap-1 font-semibold group-hover:translate-x-1 transition-transform">
                        {soon ? "Konsultasi Vendor Ini" : "Lihat Paket & Harga"} <ArrowRight className="w-3 h-3"/>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Promo Intimade */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16" data-testid="section-intimade">
        <Reveal className="rounded-3xl border border-[#E5D9C4] bg-white p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center luxury-shadow">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-2">Intimate Wedding</div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium">Paket Intimade — untuk 50 sampai 300 tamu</h2>
            <p className="mt-3 text-sm md:text-base text-[#5C4A3D] font-light max-w-2xl">
              Pernikahan hangat dengan tamu terbatas tanpa mengorbankan kualitas: rias, dekorasi, dokumentasi, dan koordinasi acara dari tim yang sama.
            </p>
          </div>
          <Link to="/paket-wedding-dwg?q=Intimade" className="inline-flex items-center gap-2 bg-[#8B5A2A] text-white px-7 py-3.5 rounded-full font-semibold hover:bg-[#1E1611] transition-colors text-xs uppercase tracking-[0.15em] group" data-testid="btn-intimade">
            <Users className="w-4 h-4"/> Lihat Paket Intimade <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"/>
          </Link>
        </Reveal>
      </section>

      {(cms.promo_title || editMode) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="promo-section">
          <Reveal className="bg-gradient-to-r from-[#8B5A2A] to-[#C4A57B] rounded-3xl p-8 md:p-12 text-white">
            <div className="inline-block text-[10px] uppercase tracking-[0.3em] mb-3 opacity-80">Promo Spesial</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight">
              <EditableText editMode={editMode} value={cms.promo_title} placeholder="(Judul promo — klik untuk isi)"
                onSave={saveText("promo_title")} className="text-white" testid="edit-promo-title"/>
            </h3>
            <p className="mt-4 max-w-2xl font-light opacity-90">
              <EditableText editMode={editMode} multiline value={cms.promo_body} placeholder="(Deskripsi promo — klik untuk isi)"
                onSave={saveText("promo_body")} className="text-white" testid="edit-promo-body"/>
            </p>
          </Reveal>
        </section>
      )}

      {cms.testimonials?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="testimonials-section">
          <Reveal className="text-center mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold">Testimoni</div>
            <h3 className="font-serif text-3xl md:text-4xl mt-2">Kata Mereka</h3>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {cms.testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-white rounded-2xl p-6 border border-[#E5D9C4] shadow-sm">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating || 5 }).map((_, ii) => <Star key={ii} className="w-3.5 h-3.5 fill-[#C4A57B] text-[#C4A57B]"/>)}
                </div>
                <p className="text-sm text-[#5C4A3D] italic leading-relaxed">"{t.quote}"</p>
                <div className="mt-4 pt-3 border-t border-[#F0EBE0] text-xs font-semibold text-[#1E1611]">— {t.name}</div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <FaqSection />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <Reveal className="relative overflow-hidden rounded-3xl bg-[#1E1611] text-[#F7F1E8]">
          <img src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80" alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25"/>
          <div className="relative z-10 p-10 md:p-16 max-w-2xl">
            <Heart className="w-8 h-8 mb-4 text-[#C4A57B]"/>
            <h3 className="font-serif text-4xl md:text-5xl leading-tight">Ceritakan rencana Anda, kami bantu sisanya.</h3>
            <p className="mt-5 text-[#E5D9C4] font-light text-sm md:text-base">Konsultasi gratis — isi form singkat atau chat WhatsApp, tim kami bantu pilih paket yang pas dengan tanggal, jumlah tamu, dan budget Anda.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/consultation" className="inline-flex items-center gap-2 bg-[#C4A57B] text-[#1E1611] px-8 py-3.5 rounded-full font-semibold hover:bg-white transition-colors text-sm uppercase tracking-widest" data-testid="btn-cta-consult">
                <CalendarHeart className="w-4 h-4"/> Konsultasi Gratis
              </Link>
              <a href={`https://wa.me/${cms.contact_wa}?text=${encodeURIComponent(cms.cta_wa_message || "Halo DWG, saya mau konsultasi paket wedding.")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#C4A57B] text-[#C4A57B] px-8 py-3.5 rounded-full font-semibold hover:bg-[#C4A57B] hover:text-[#1E1611] transition-colors text-sm uppercase tracking-widest" data-testid="btn-cta-wa">
                <MessageCircle className="w-4 h-4"/> WhatsApp
              </a>
              <a href="https://instagram.com/mua.dessywedding" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#C4A57B] text-[#C4A57B] px-8 py-3.5 rounded-full font-semibold hover:bg-[#C4A57B] hover:text-[#1E1611] transition-colors text-sm uppercase tracking-widest" data-testid="btn-cta-ig">
                <Instagram className="w-4 h-4"/> Instagram
              </a>
              <a href="https://www.tiktok.com/@dessyweddinggarut.mua" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#C4A57B] text-[#C4A57B] px-8 py-3.5 rounded-full font-semibold hover:bg-[#C4A57B] hover:text-[#1E1611] transition-colors text-sm uppercase tracking-widest" data-testid="btn-cta-tiktok">
                <Music2 className="w-4 h-4"/> TikTok
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <PublicFooter contact={cms} />
      {isOwner && <OwnerEditToggle editMode={editMode} setEnabled={setEnabled}/>}
    </div>
  );
}
